import { useState, useEffect } from 'react';
import { api } from '../../api/api';
import AdminHeader from '../../components/adminHeader';
import AdminFooter from '../../components/adminFooter';
import EditGiveawayAssetModal from '../../components/modal/EditGiveawayAssetModal';
import CreateGiveawayAssetModal from '../../components/modal/CreateGiveawayAssetModal';
import ViewGiveawayAssetModal from '../../components/modal/ViewGiveawayAssetModal';

interface GiveawayAsset {
  id: number;
  name: string;
  description: string;
  image_path: string;
  price: number;
  category: string;
  reward_id: string;
  status: 'claimed' | 'issuedtomarketors' | 'notclaimed';
  created_at: string;
}

const ManageGiveawayAssets = () => {
  const [giveawayAssets, setGiveawayAssets] = useState<GiveawayAsset[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredGiveawayAssets = giveawayAssets.filter(asset =>
    asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [currentGiveawayAsset, setCurrentGiveawayAsset] = useState<GiveawayAsset | null>(null);

  const fetchGiveawayAssets = async () => {
    try {
      const response = await api.get('/api/all/giveaway_assets');
      const fetchedAssets = response.data.map((asset: GiveawayAsset) => ({
        ...asset,
        price: parseFloat(asset.price as any), // Ensure price is a number
      }));
      setGiveawayAssets(fetchedAssets);
    } catch (error) {
      console.error('Failed to fetch giveaway assets', error);
    }
  };

  const handleCreateGiveawayAsset = async (newAsset: Omit<GiveawayAsset, 'id' | 'created_at'>) => {
    try {
      await api.post('/api/all/giveaway_assets', newAsset);
      fetchGiveawayAssets();
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Failed to create giveaway asset', error);
    }
  };

  const handleUpdateGiveawayAsset = async (id: number, updatedAsset: Partial<GiveawayAsset>) => {
    try {
      await api.patch(`/api/all/giveaway_assets/${id}`, updatedAsset);
      fetchGiveawayAssets();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Failed to update giveaway asset', error);
    }
  };

  const handleDeleteGiveawayAsset = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this asset?')) {
      try {
        await api.delete(`/api/all/giveaway_assets/${id}`);
        fetchGiveawayAssets();
      } catch (error) {
        console.error('Failed to delete giveaway asset', error);
      }
    }
  };

  const openEditModal = (asset: GiveawayAsset) => {
    setCurrentGiveawayAsset(asset);
    setIsEditModalOpen(true);
  };

  const openViewModal = (asset: GiveawayAsset) => {
    setCurrentGiveawayAsset(asset);
    setIsViewModalOpen(true);
  };

  useEffect(() => {
    fetchGiveawayAssets();
  }, []);

  return (
    <div className="w-full h-full flex flex-col bg-[#00000071]">
      <AdminHeader />
      <div
        style={{
          backgroundImage: "url(/assets/img/landing/landing-bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        className="flex-grow flex flex-col items-center justify-center w-full relative pt-6 px-4"
      >
        <div className="w-full max-w-6xl gothic-panel max-h-[90vh] overflow-y-auto">
          <h1 className="gothic-heading">Manage Giveaway Assets</h1>
          <div className="flex justify-between mb-4">
            <button onClick={() => setIsCreateModalOpen(true)} className="gothic-button gothic-button-primary">
              Add New Asset
            </button>
            <input
              type="text"
              placeholder="Search assets..."
              className="gothic-input w-1/3"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-8">
            {filteredGiveawayAssets.map((asset) => (
              <div key={asset.id} className="gothic-panel p-4 flex flex-col justify-between hover:bg-gray-800 transition duration-200">
                <div>
                  {asset.image_path && (
                    <img
                      src={`${process.env.REACT_APP_API_URL}/api/files/${asset.image_path}`}
                      alt={asset.name}
                      className="w-full h-48 object-cover rounded mb-4"
                    />
                  )}
                  <h2 className="text-xl gothic-text-gold mb-2 truncate">{asset.name}</h2>
                  <p className="gothic-text-silver mb-4 text-sm h-20 overflow-y-auto">{asset.description}</p>

                  <div className="grid grid-cols-2 gap-x-2 gap-y-1 mb-4 text-sm">
                    <span className="gothic-text-silver font-semibold">ID:</span><span className="gothic-text-gold truncate">{asset.id}</span>
                    <span className="gothic-text-silver font-semibold">Price:</span><span className="gothic-text-gold truncate">${asset.price}</span>
                    <span className="gothic-text-silver font-semibold">Category:</span><span className="gothic-text-gold truncate">{asset.category}</span>
                    <span className="gothic-text-silver font-semibold">Status:</span><span className="gothic-text-gold truncate">{asset.status}</span>
                  </div>
                  <div className="text-sm mb-4">
                    <span className="gothic-text-silver font-semibold">Reward ID:</span>
                    <span className="gothic-text-gold ml-2 truncate">{asset.reward_id}</span>
                  </div>

                  <p className="gothic-text-silver text-xs">Created: {new Date(asset.created_at).toLocaleString()}</p>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button onClick={() => openViewModal(asset)} className="gothic-button bg-gray-700 border-gray-600">
                    View
                  </button>
                  <button onClick={() => openEditModal(asset)} className="gothic-button bg-gray-700 border-gray-600">
                    Edit
                  </button>
                  <button onClick={() => handleDeleteGiveawayAsset(asset.id)} className="gothic-button bg-gray-700 border-gray-600">
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {isCreateModalOpen && (
        <CreateGiveawayAssetModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateGiveawayAsset}
        />
      )}
      {isEditModalOpen && currentGiveawayAsset && (
        <EditGiveawayAssetModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          asset={currentGiveawayAsset}
          onSave={handleUpdateGiveawayAsset}
        />
      )}
      {isViewModalOpen && currentGiveawayAsset && (
        <ViewGiveawayAssetModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          asset={currentGiveawayAsset}
        />
      )}
      <AdminFooter />
    </div>
  );
};

export default ManageGiveawayAssets;
